import React, { FC, useCallback } from "react";
import {
  ContentWrapper,
  Heading,
} from "containers/RightPanel/RightPanel.styles";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { OpenRightPanel } from "types/common/rightPanel";
import * as yup from "yup";
import { useCurrencySymbol } from "hooks/useCurrencySymbol";
import { useBooksPayPalAvailability } from "../../../../branding/usePayPalAvailability";
import PayPalOrderButton from "../../../../components/PayPalOrderButton/PayPalOrderButton";
import { getProfile } from "../../../../reduxFolder/reducers/auth";
import { openRightPanel } from "../../../../reduxFolder/reducers/rightPanel";
import {
  authPaypalFeePercent,
  authSelectUser,
} from "../../../../reduxFolder/selectors/globalSelectors";
import api from "../../../../utils/api";
import formatter from "../../../../utils/formatter";
import { toFiniteNumber } from "../../../../utils/number";
import { calculatePaymentTotal } from "../../../../utils/paymentFees";
import { extractPayPalOrderId } from "../../../../utils/paypal";
import { toastResponseSuccess } from "../../../../utils/responseMessageHelper";
import { createAddFundsAmountValidation } from "../../helpers/addFundsAmountValidation";
import {
  BooksBalanceLayout,
  BooksBalancePanelSection,
  ContentContainer,
  FormWrapper,
} from "../../styles/BooksAddFunds.style";
import BooksBalance from "../BooksBalance/BooksBalance";
import BooksAddPayPalFundsForm from "./BooksAddPayPalFundsForm";

interface BooksAddFundsPanelProps {
  panelData: any;
  authPaypalFeePercent: string;
  getProfile: () => Promise<void>;
  openRightPanel: OpenRightPanel;
  user: any;
}

const BooksAddFundsPanel: FC<BooksAddFundsPanelProps> = React.memo(
  ({ panelData, authPaypalFeePercent, getProfile, openRightPanel, user }) => {
    const { t } = useTranslation("common");
    const currencySymbol = useCurrencySymbol();
    const restoredValues = panelData.get("formValues");
    const { config: paypalConfig, isAvailable: isPayPalAvailable } =
      useBooksPayPalAvailability(user);

    const { errors, values, touched, isValid, resetForm, setFieldValue } =
      useFormik({
        initialValues: {
          amount: restoredValues?.amount ?? "",
        },
        validationSchema: yup.object({
          amount: createAddFundsAmountValidation(t, currencySymbol),
        }),
        onSubmit: () => undefined,
      });
    const { amount } = values;
    const amountFloat = toFiniteNumber(amount);
    const total = calculatePaymentTotal(amountFloat, authPaypalFeePercent);
    const helpPanelContext = {
      openRightPanel,
      returnPanelType: "BOOKS_ADD_PAYPAL_FUNDS",
      getReturnPanelData: () => panelData.set("formValues", values),
    };

    const formattedTotal = `${currencySymbol}${formatter.currency(total)}`;
    const payBtnTitle = `${t("common.processPaypal")} (${formattedTotal})`;
    const createPayPalOrder = useCallback(async () => {
      const response = await api.recharge.rechargePayPalCreateOrder({
        amount: amountFloat,
      });
      return extractPayPalOrderId(response);
    }, [amountFloat]);

    const capturePayPalOrder = useCallback(
      async (orderId: string) => {
        await api.recharge.rechargePayPalComplete({ order_id: orderId });
        resetForm();
        getProfile();
        toastResponseSuccess(
          t("books.paypal.successfulTransactions", { amount, currencySymbol }),
        );
      },
      [amount, currencySymbol, getProfile, resetForm, t],
    );

    if (!isPayPalAvailable) return null;

    return (
      <ContentWrapper>
        <Heading $isBordered>{t("books.addFundsPanel.chargePaypal")}</Heading>
        <ContentContainer>
          <FormWrapper>
            <BooksBalancePanelSection>
              <BooksBalanceLayout>
                <BooksBalance balance={user.get("balance")} />
              </BooksBalanceLayout>
            </BooksBalancePanelSection>
            <BooksAddPayPalFundsForm
              values={values}
              errors={errors}
              touched={touched}
              setFieldValue={setFieldValue}
            />
          </FormWrapper>
          <PayPalOrderButton
            paypalConfig={paypalConfig}
            createOrder={createPayPalOrder}
            captureOrder={capturePayPalOrder}
            fallbackChildren={payBtnTitle}
            disabled={!isValid || !paypalConfig || amountFloat <= 0}
            helpPanelContext={helpPanelContext}
          />
        </ContentContainer>
      </ContentWrapper>
    );
  },
);

const withConnect = connect(
  createStructuredSelector({
    authPaypalFeePercent,
    user: authSelectUser,
  }),
  {
    getProfile,
    openRightPanel,
  },
);

export default withConnect(BooksAddFundsPanel);
